import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isEstablishmentRouteContext } from '../utils/slugHelper';
import { LoadingSpinner } from '../components/UI/LoadingSpinner';

import ProtectedRoute from '../components/auth/ProtectedRoute';
import AuthenticatedRouteGuard from '../components/auth/AuthenticatedRouteGuard';

const PageFallback = () => (
  <div
    className="flex min-h-[50vh] items-center justify-center"
    aria-busy="true"
    aria-label="Carregando"
  >
    <LoadingSpinner size="lg" />
  </div>
);

const HomePage = lazy(() => import('../pages/HomePage'));
const PricingPage = lazy(() => import('../pages/PricingPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const EstablishmentHomePage = lazy(() => import('../pages/EstablishmentHomePage'));
const EstablishmentLoginPage = lazy(() => import('../pages/EstablishmentLoginPage'));
const InviteRegisterPage = lazy(() => import('../pages/InviteRegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const OnboardingPage = lazy(() => import('../pages/OnboardingPage'));
const AppointmentsPage = lazy(() => import('../pages/AppointmentsPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const CustomersPage = lazy(() => import('../pages/CustomersPage'));
const WorksPage = lazy(() => import('../pages/WorksPage'));
const StockPage = lazy(() => import('../pages/StockPage'));
const ProfessionalsManagementPage = lazy(() => import('../pages/ProfessionalsManagementPage'));
const BillingPage = lazy(() => import('../pages/BillingPage'));
const SubscriptionPage = lazy(() => import('../pages/SubscriptionPage'));
const NotificationPage = lazy(() => import('../pages/NotificationPage'));
const PaymentConfigPage = lazy(() => import('../pages/PaymentConfigPage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage'));
const HelpCenterPage = lazy(() => import('../pages/HelpCenterPage'));
const SupportPage = lazy(() => import('../pages/SupportPage'));
const AccessControlPage = lazy(() => import('../pages/AccessControlPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const EstablishmentNotFoundPage = lazy(() => import('../pages/EstablishmentNotFoundPage'));
const ApplicationUnavailablePage = lazy(() => import('../pages/ApplicationUnavailablePage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('../pages/TermsOfUsePage'));
const DataDeletionPage = lazy(() => import('../pages/DataDeletionPage'));

const AppRoutes: React.FC = () => {
  const isEstablishment = isEstablishmentRouteContext();

  if (isEstablishment) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<EstablishmentHomePage />} />
          <Route path="/login" element={<EstablishmentLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/invite" element={<InviteRegisterPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacidade" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/termos" element={<Navigate to="/terms" replace />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/exclusao-de-dados" element={<Navigate to="/data-deletion" replace />} />

          <Route
            path="/dashboard"
            element={
              <AuthenticatedRouteGuard>
                <DashboardPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/onboarding"
            element={
              <AuthenticatedRouteGuard>
                <OnboardingPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/appointments"
            element={
              <AuthenticatedRouteGuard>
                <AppointmentsPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/customers"
            element={
              <AuthenticatedRouteGuard>
                <CustomersPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/works"
            element={
              <AuthenticatedRouteGuard>
                <WorksPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/stock"
            element={
              <AuthenticatedRouteGuard>
                <StockPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/professionals"
            element={
              <AuthenticatedRouteGuard>
                <ProfessionalsManagementPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/billing"
            element={
              <AuthenticatedRouteGuard>
                <BillingPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/subscription"
            element={
              <AuthenticatedRouteGuard>
                <SubscriptionPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/notifications"
            element={
              <AuthenticatedRouteGuard>
                <NotificationPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/payment-config"
            element={
              <AuthenticatedRouteGuard>
                <PaymentConfigPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/reports"
            element={
              <AuthenticatedRouteGuard>
                <ReportsPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthenticatedRouteGuard>
                <SettingsPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/access-control"
            element={
              <AuthenticatedRouteGuard>
                <AccessControlPage />
              </AuthenticatedRouteGuard>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route path="/not-found" element={<NotFoundPage />} />
          <Route path="/establishment-not-found" element={<EstablishmentNotFoundPage />} />
          <Route path="/service-unavailable" element={<ApplicationUnavailablePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<EstablishmentLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite" element={<InviteRegisterPage />} />
        <Route path="/help" element={<HelpCenterPage />} />

        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacidade" element={<Navigate to="/privacy" replace />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/termos" element={<Navigate to="/terms" replace />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/exclusao-de-dados" element={<Navigate to="/data-deletion" replace />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <ProtectedRoute>
              <AppointmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />
        <Route path="/not-found" element={<NotFoundPage />} />
        <Route path="/establishment-not-found" element={<EstablishmentNotFoundPage />} />
        <Route path="/service-unavailable" element={<ApplicationUnavailablePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
